import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { useBasket } from "../context/BasketContext";
import { PublicLayout } from "../components/public/PublicLayout";
import { ProductCard } from "../components/public/ProductCard";
import { toast } from "sonner";
import { ChevronLeft, MapPin, Check, Plus, Minus } from "lucide-react";

export default function ProductDetail() {
  const { slug } = useParams();
  const { t, tr } = useLang();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { add } = useBasket();
  const [qty, setQty] = React.useState(1);

  const { data: product, isLoading, isError } = useQuery({
    queryKey: ["product", slug], queryFn: async () => (await api.get(`/products/${slug}`)).data,
  });
  const { data: related = [] } = useQuery({
    queryKey: ["related", product?.category], enabled: !!product,
    queryFn: async () => (await api.get(`/products?category=${encodeURIComponent(product.category)}`)).data,
  });
  // Trade view: fetch catalogue entry (with price/stock) if approved partner
  const isPartner = user && user.role === "partner" && user.account_status === "Approved";
  const { data: tradeItem } = useQuery({
    queryKey: ["trade-product", slug, product?.id], enabled: !!product && isPartner,
    queryFn: async () => {
      const list = (await api.get("/trade/catalogue")).data;
      return list.find((p) => p.id === product.id);
    },
  });

  useEffect(() => { if (product) document.title = `${product.name} | Sambiva`; }, [product]);

  if (isLoading) return <PublicLayout><div className="max-w-6xl mx-auto px-6 py-24 text-center text-sambiva-muted">{t("common.loading")}</div></PublicLayout>;
  if (isError || !product) return <PublicLayout><div className="max-w-6xl mx-auto px-6 py-24 text-center">Product not found.</div></PublicLayout>;

  const name = tr(product.translations, "name", product.name);
  const desc = tr(product.translations, "description", product.description);

  const spec = (label, val) => val ? (
    <div className="flex justify-between py-3 border-b border-sambiva-charcoal/8">
      <span className="text-sambiva-muted">{label}</span>
      <span className="text-sambiva-charcoal font-medium text-right">{val}</span>
    </div>
  ) : null;

  return (
    <PublicLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <button onClick={() => navigate(-1)} data-testid="back-btn" className="flex items-center gap-1 text-sambiva-muted hover:text-sambiva-red mb-6 text-sm">
          <ChevronLeft size={16} /> {t("common.back")}
        </button>

        <div className="grid md:grid-cols-2 gap-10">
          <div>
            <div className="bg-white rounded-3xl overflow-hidden aspect-square border border-sambiva-charcoal/5">
              <img src={product.images?.[0]} alt={name} className="w-full h-full object-cover" />
            </div>
          </div>

          <div>
            <span className="text-sm font-semibold text-sambiva-green uppercase tracking-wide">{product.category}</span>
            <h1 className="font-serif text-3xl sm:text-4xl text-sambiva-charcoal mt-1 mb-4">{name}</h1>
            <p className="text-sambiva-muted text-lg leading-relaxed mb-6">{desc}</p>

            {isPartner && tradeItem ? (
              <div data-testid="trade-purchase-box" className="bg-white rounded-2xl p-5 border-2 border-sambiva-green/20 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-xs text-sambiva-muted uppercase tracking-wide">Trade Price</p>
                    <p className="text-2xl font-bold text-sambiva-green">€{tradeItem.trade_price?.toFixed(2)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-sambiva-muted uppercase tracking-wide">Case Size</p>
                    <p className="font-semibold text-sambiva-charcoal">{tradeItem.case_quantity} units</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-sambiva-muted uppercase tracking-wide">Stock</p>
                    <p className="font-semibold text-sambiva-charcoal">{tradeItem.stock_quantity}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center border border-sambiva-charcoal/15 rounded-full">
                    <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-2.5"><Minus size={16} /></button>
                    <span className="w-10 text-center font-semibold" data-testid="detail-qty">{qty}</span>
                    <button onClick={() => setQty((q) => q + 1)} className="p-2.5"><Plus size={16} /></button>
                  </div>
                  <button
                    data-testid="add-to-trade-order"
                    onClick={() => { add(tradeItem, qty); toast.success("Product added to order."); }}
                    className="flex-1 bg-sambiva-green hover:bg-sambiva-deepgreen text-white font-semibold py-3 rounded-full transition-colors"
                  >
                    Add to Trade Order
                  </button>
                </div>
              </div>
            ) : (
              <button
                data-testid="detail-where-to-buy"
                onClick={() => navigate("/where-to-buy")}
                className="inline-flex items-center gap-2 bg-sambiva-red hover:bg-sambiva-deepred text-white font-semibold px-6 py-3 rounded-full mb-6 transition-colors"
              >
                <MapPin size={18} /> Where to Buy
              </button>
            )}

            <div className="text-sm">
              {spec("Pack Size", product.pack_size)}
              {spec("Unit Weight", product.unit_weight)}
              {spec("Origin", product.origin)}
              {spec("Storage", product.storage)}
              {spec("Ingredients", product.ingredients)}
              {spec("Allergens", product.allergens)}
              {spec("Preparation", product.preparation)}
              {spec("Nutrition", product.nutrition)}
            </div>

            {product.dietary?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-5">
                {product.dietary.map((d) => (
                  <span key={d} className="flex items-center gap-1 text-xs font-medium bg-sambiva-green/10 text-sambiva-green px-3 py-1.5 rounded-full">
                    <Check size={13} /> {d}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {related.filter((r) => r.id !== product.id).length > 0 && (
          <div className="mt-20">
            <h2 className="font-serif text-2xl sm:text-3xl text-sambiva-charcoal mb-8">You may also like</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {related.filter((r) => r.id !== product.id).slice(0, 4).map((p) => (
                <ProductCard key={p.id} product={p} t={t} tr={tr} onView={(s) => navigate(`/products/${s}`)} />
              ))}
            </div>
          </div>
        )}
      </div>
    </PublicLayout>
  );
}
